import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SuccessIcon from '@mui/icons-material/CheckCircle';
import { getApiErrorInfo } from '../../services/api';
import courseService from '../../services/courseService';

export default function SearchIndexManagement() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ message: string; count: number } | null>(null);

  async function handleReindex() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await courseService.reindexCourses();
      setResult(data);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Reindex failed. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box>
      {/* Action toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={handleReindex}
          disabled={loading}
        >
          {loading ? 'Indexing…' : 'Rebuild Index'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Rebuild the Meilisearch index from the current course database. Run this after bulk imports or if search results seem stale.
      </Typography>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Success result */}
      {result && (
        <Paper
          variant="outlined"
          sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          <SuccessIcon color="success" />
          <Typography variant="body1" fontWeight={500}>
            Index rebuilt successfully
          </Typography>
          <Chip
            label={`${result.count} courses indexed`}
            color="success"
            size="small"
          />
        </Paper>
      )}

      {/* Loading state */}
      {loading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <CircularProgress size={36} sx={{ mb: 2 }} />
          <Typography>Indexing courses…</Typography>
        </Paper>
      )}

      {/* Info panel */}
      {!loading && !result && !error && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <SearchIcon color="primary" />
            <Typography variant="body1" fontWeight={500}>
              About the Search Index
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The search index powers typo-tolerant, relevance-ranked course search across the platform.
            It is kept in sync automatically when courses are created, updated, or deleted.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use <strong>Rebuild Index</strong> to do a full resync, for example after a Docebo import
            or if search results appear out of date.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
